import React, { useEffect, useState } from "react";
import { fetchCaseRange, fetchReportGoals } from "../../../services/httpClient";
import { SingleCardMetric } from "../ui/SingleCardMetric";
import { formatDateTimeToLocal } from "../../../helpers/date";

interface GoalsCompletedPercentageMetricState {
  dataCases?: any[];
  dataGoals?: any[];
  readOnly?: boolean;
  setSelectedData: (data: any[] | null) => void;
  setDataHeaders: (data: any[] | null) => void;
  active?: boolean;
  useDatePicker?: boolean;
  startDate?: string;
  endDate?: string;
  onHandleClick?: any;
}

export const GoalsCompletedPercentageMetric = ({
  dataCases,
  dataGoals,
  readOnly = true,
  setSelectedData,
  setDataHeaders,
  active,
  useDatePicker = false,
  startDate,
  endDate,
  onHandleClick,
}: GoalsCompletedPercentageMetricState) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cases, setCases] = useState<any[]>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [casesCompletingGoals, setCasesCompletingGoals] = useState<any[]>();
  const [percent, setPercent] = useState<string>();

  const tableHeaders = [
    { label: "Case Name", key: "case_name" },
    { label: "Goals Completed", key: "goals_completed" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      if ((dataCases || dataCases === 0) && (dataGoals || dataGoals === 0)) {
        setCases(dataCases);
        setCasesCompletingGoals(dataGoals);
        const percentage = ((dataGoals.length / dataCases.length) * 100).toFixed(1);
        setPercent(percentage.toString());
        setIsLoading(false);
      } else {
        //default dates
        const starting = new Date();
        starting.setDate(starting.getDate() - 365);
        var start = starting.toISOString().slice(0, 10);
        var end = new Date().toISOString().slice(0, 10);
        if (useDatePicker) {
          start = startDate ?? start;
          end = endDate ?? end;
        }
        const cases = await getCaseRange(start!, end!);
        const completingGoals = await getGoals(start!, end!);
        const percentage = ((completingGoals.length / cases.length) * 100).toFixed(1);
        setPercent(percentage.toString());
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dataCases, dataGoals, startDate, endDate]);

  const getCaseRange = async (start: string, end: string): Promise<any[]> => {
    try {
      const res = await fetchCaseRange(start, end);
      const caseData = res[0].data;
      setCases(caseData);
      return caseData;
    } catch (err: any) {
      console.log(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getGoals = async (start: string, end: string): Promise<any[]> => {
    try {
      const res = await fetchReportGoals(start, end);
      const goals = res[0]?.data;
      const sortedGoals = goals.sort((a: any, b: any) => (a.completed_at < b.completed_at ? 1 : -1));
      sortedGoals.forEach((goal: any) => {
        if (goal?.completed_at) {
          const localDateTime = formatDateTimeToLocal(goal.completed_at);
          goal.completed_at_formatted = localDateTime.date + " " + localDateTime.time;
        }
        if (goal?.created_at) {
          const localDateTime = formatDateTimeToLocal(goal.created_at);
          goal.created_at_formatted = localDateTime.date + " " + localDateTime.time;
        }
      });

      const completingCases = uniqueCasesWithCompletedGoals(sortedGoals);
      setCasesCompletingGoals(completingCases);
      return completingCases;
    } catch (err: any) {
      console.log(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const uniqueCasesWithCompletedGoals = (sortedGoals: any[]) =>
    Object.values(
      sortedGoals
        .filter((g: any) => g.completed_at)
        .reduce((acc: any, g: any) => {
          if (acc[g.case_id]) {
            acc[g.case_id].goals_completed++;
          } else {
            acc[g.case_id] = { case_id: g.case_id, case_name: g.case_name, goals_completed: 1 };
          }
          return acc;
        }, {}),
    );

  const onViewDetails = () => {
    if (!active) {
      setSelectedData(casesCompletingGoals || null);
    } else {
      setSelectedData(null);
    }

    setDataHeaders(tableHeaders);
    onHandleClick();
  };

  if (readOnly) {
    return <SingleCardMetric title={"Case Goal Completion %"} total={percent} isLoading={isLoading} />;
  } else {
    return (
      <SingleCardMetric
        title={"Case Goal Completion %"}
        total={percent}
        onHandleClick={onViewDetails}
        cursor={true}
        active={active}
        isLoading={isLoading}
      />
    );
  }
};
